import { useEffect, useState, useCallback, Fragment } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import { ModeProvider, useMode } from "./context/ModeContext";
import { ContentProvider, useContent } from "./context/ContentContext";
import { ModePicker } from "./components/ModePicker";
import { JuniorMural } from "./components/JuniorMural";
import DailyChallenge from "./pages/DailyChallenge";
import AdminDashboard from "./pages/AdminDashboard";
import AdminBlocksSolution from "./pages/AdminBlocksSolution";
import Rankings from "./pages/Rankings";
import Instructions from "./pages/Instructions";
import About from "./pages/About";
import VerifyEmail from "./pages/VerifyEmail";
import ResetPassword from "./pages/ResetPassword";
import Subscription from "./pages/Subscription";
import SchoolApply from "./pages/SchoolApply";
import CreateQuestion from "./pages/CreateQuestion";
import Profile from "./pages/Profile";
import ManageStudents from "./pages/ManageStudents";
import JoinSchool from "./pages/JoinSchool";
import PlatformInvite from "./pages/PlatformInvite";
import AuthCallback from "./components/AuthCallback";
import ThankYouRegistration from "./pages/ThankYouRegistration";
import ThankYouSubscription from "./pages/ThankYouSubscription";
import LiveJoin from "./pages/LiveJoin";
import LivePlayer from "./pages/LivePlayer";
import LiveHost from "./pages/LiveHost";
import { purgeLegacyDrafts } from "./lib/blocksDraft";
import "./App.css";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

// Use empty string for same-origin API calls (works on both preview and production)
const BACKEND_URL = '';
const API = `${BACKEND_URL}/api`;

function AppRouter() {
  const location = useLocation();
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem("darkMode");
    return saved ? JSON.parse(saved) : false;
  });
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [sessionInvalidated, setSessionInvalidated] = useState(false);

  // Old block drafts were stored per question only, so previous students' answers reappeared on shared devices
  useEffect(() => { purgeLegacyDrafts(); }, []);

  // Handle session invalidation (user logged in elsewhere)
  const handleSessionInvalidation = useCallback((reason) => {
    setUser(null);
    setSessionInvalidated(true);
    
    // Clear any stored session data
    sessionStorage.removeItem('dailyCodingSession');
    
    // Show appropriate message based on reason
    if (reason === 'session_invalidated') {
      toast.error(
        "You've been signed out because your account was accessed from another device. Only one active session is allowed.",
        { duration: 8000 }
      );
    } else if (reason === 'session_expired') {
      toast.info("Your session has expired. Please sign in again.", { duration: 5000 });
    }
    
    // Reset the flag after showing the message
    setTimeout(() => setSessionInvalidated(false), 1000);
  }, []);

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
    localStorage.setItem("darkMode", JSON.stringify(darkMode));
  }, [darkMode]);

  useEffect(() => {
    // Skip auth check if we're in OAuth callback
    if (location.hash?.includes("session_id=")) {
      setAuthChecked(true);
      return;
    }
    checkAuth();
  }, [location.hash]);

  // Periodic session validation for logged-in users (every 2 minutes)
  useEffect(() => {
    if (!user) return;
    
    const validateSession = async () => {
      try {
        const response = await fetch(`${API}/auth/me`, {
          credentials: "include",
        });
        
        if (response.status === 401) {
          const errorData = await response.json();
          if (errorData.detail === 'session_invalidated' || errorData.detail === 'session_expired') {
            handleSessionInvalidation(errorData.detail);
          }
        }
      } catch {
        // Network error - don't invalidate
      }
    };
    
    // Check session every 2 minutes
    const intervalId = setInterval(validateSession, 2 * 60 * 1000);
    
    return () => clearInterval(intervalId);
  }, [user, handleSessionInvalidation]);

  const checkAuth = async (previousUser = null) => {
    try {
      const response = await fetch(`${API}/auth/me`, {
        credentials: "include",
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else if (response.status === 401) {
        // Check for session invalidation reasons
        try {
          const errorData = await response.json();
          if (errorData.detail === 'session_invalidated' || errorData.detail === 'session_expired') {
            // Only show message if user was previously logged in
            if (previousUser) {
              handleSessionInvalidation(errorData.detail);
            }
          }
        } catch {
          // Not authenticated - normal state
        }
        setUser(null);
      }
    } catch (error) {
      // Network error or not authenticated
      setUser(null);
    } finally {
      setAuthChecked(true);
    }
  };

  const handleMergeSession = () => {
    // Refresh user stats after session merge
    checkAuth();
    setRefreshKey(prev => prev + 1);
  };

  const handleScoreUpdate = () => {
    // Refresh user stats after points are earned
    setRefreshKey(prev => prev + 1);
  };

  // Check for session_id in URL fragment (OAuth callback)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback setUser={setUser} />;
  }

  return (
    <ContentProvider>
    <ModeProvider user={user}>
    <div className="min-h-screen bg-background flex flex-col relative isolate">
      <JuniorMural />
      <ModeGate pathname={location.pathname} />
      <Navbar 
        darkMode={darkMode} 
        setDarkMode={setDarkMode} 
        user={user} 
        setUser={setUser}
        refreshKey={refreshKey}
      />
      <main className="flex-1">
        <Routes>
          <Route path="/" element={<ModeKeyed><DailyChallenge user={user} onMergeSession={handleMergeSession} onScoreUpdate={handleScoreUpdate} /></ModeKeyed>} />
          <Route path="/rankings" element={<ModeKeyed><Rankings user={user} /></ModeKeyed>} />
          <Route path="/instructions" element={<Instructions user={user} />} />
          <Route path="/about" element={<About user={user} />} />
          <Route path="/verify-email" element={<VerifyEmail setUser={setUser} checkAuth={checkAuth} />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/subscription" element={<Subscription user={user} setUser={setUser} authChecked={authChecked} />} />
          <Route path="/subscription/success" element={<Subscription user={user} setUser={setUser} authChecked={authChecked} />} />
          <Route path="/subscription/cancel" element={<Subscription user={user} setUser={setUser} authChecked={authChecked} />} />
          <Route path="/school-apply" element={<SchoolApply user={user} setUser={setUser} />} />
          <Route path="/create-question" element={<CreateQuestion user={user} setUser={setUser} />} />
          <Route path="/profile" element={<Profile user={user} setUser={setUser} authChecked={authChecked} />} />
          <Route path="/manage-students" element={<ManageStudents user={user} authChecked={authChecked} />} />
          <Route path="/join-school" element={<JoinSchool user={user} setUser={setUser} />} />
          <Route path="/join" element={<LiveJoin user={user} />} />
          <Route path="/live/play" element={<LivePlayer />} />
          <Route path="/live-host" element={<LiveHost user={user} />} />
          <Route path="/register" element={<PlatformInvite setUser={setUser} />} />
          <Route path="/admin/blocks-solution/:questionId" element={<AdminBlocksSolution user={user} />} />
          <Route path="/admin" element={<AdminDashboard user={user} setUser={setUser} />} />
          <Route path="/admin/*" element={<AdminDashboard user={user} setUser={setUser} />} />
          {/* Thank You Pages for Analytics Tracking */}
          <Route path="/welcome" element={<ThankYouRegistration user={user} />} />
          <Route path="/thank-you-subscription" element={<ThankYouSubscription user={user} setUser={setUser} />} />
        </Routes>
      </main>
      <Footer />
      <Toaster position="top-center" />
    </div>
    </ModeProvider>
    </ContentProvider>
  );
}

// Remount mode-dependent pages when the coding mode toggles so they refetch their track
function ModeKeyed({ children }) {
  const { mode } = useMode();
  return <Fragment key={mode}>{children}</Fragment>;
}

// First-visit Blocks/Python choice (skipped on join/live/auth flows so nobody is blocked mid-task)
const PICKER_EXEMPT = ["/join", "/live", "/verify-email", "/reset-password", "/register", "/welcome", "/thank-you", "/subscription"];
function ModeGate({ pathname }) {
  const { hasChosen } = useMode();
  const { loaded } = useContent();  // wait for admin-edited copy so the cards don't flash default text
  if (hasChosen || !loaded || PICKER_EXEMPT.some((p) => pathname.startsWith(p))) return null;
  return <ModePicker />;
}

function App() {
  return (
    <BrowserRouter>
      <AppRouter />
    </BrowserRouter>
  );
}

export default App;
